import React from "react";
import faCategories from "./faCategories";
import iconLibraryCategories from "./iconLibrary";

// Per-segment icon persistence resolver (icon-picker-slice / DL-045 follow-on).
//
// The SegmentIcon picker emits a React ELEMENT (`<WrappedIcon/>`) or "" for the
// "No Icon" tile, and the wheel engine (createPie.js updateIcons) reads a React
// element from `d.data.icon`. But the chosen icon must PERSIST: generatePayload
// serializes `segment.settings` to JSON, and a React element cannot round-trip
// (it saves as garbage and cannot be restored on reload). So the value stored on
// `segment.settings.icon` is a plain, serializable IDENTIFIER — the icon's
// `displayName` string (e.g. "FaBeer") — and these two pure helpers convert
// between that identifier and a React element at the picker + engine boundaries.
// This keeps the shared SegmentIcon component's element contract byte-identical.

// Flat map displayName -> wrapped icon component, built once from BOTH icon
// sources the picker can render. SegmentIcon.jsx selects its source by the
// ICON_LIBRARY_V2_ENABLED flag: legacy `faCategories` (Fa*) when OFF, the
// line-art `iconLibraryCategories` (Tb*) when ON. The resolver must cover both
// so (a) a V2 pick reads back a real element (aria-pressed flips true, the wheel
// draws the glyph) — B1 fix — and (b) legacy Fa* identifiers already persisted
// on existing segments still resolve regardless of the flag's current value.
// Merging the FLAT lists (not the keyed category objects) is collision-proof:
// a shared category key across the two sources can't drop either set's icons.
const iconComponentByName = (() => {
  const map = {};
  const addSource = (categories) => {
    Object.values(categories || {}).forEach((list) => {
      (list || []).forEach((Comp) => {
        const name = Comp && Comp.displayName;
        if (name && !map[name]) map[name] = Comp;
      });
    });
  };
  addSource(faCategories);
  addSource(iconLibraryCategories);
  return map;
})();

// Element (from the picker's onIconSelect) -> serializable identifier.
// Non-elements, null, "" -> "" (the No-Icon / cleared value).
export const iconNameFromElement = (el) => {
  if (!el || !React.isValidElement(el)) return "";
  const name = el.type && el.type.displayName;
  return typeof name === "string" ? name : "";
};

// Bug #969 round 2 (Gordon): "Segment icons should NEVER have boxes." PR
// #4390 removed FaIdBadge/FaIdCard from the picker (faCategories.js), which
// stops NEW assignments but does nothing for segments that already persisted
// one of these identifiers before the fix shipped (e.g. /builder/3169) — the
// box is baked into the FA glyph's own SVG path, not a CSS wrapper, so those
// wheels kept rendering the boxed glyph on every load. Since both names are
// now absent from iconComponentByName, an unresolved lookup would silently
// fall back to the engine's generic default icon (FaBookOpen) — losing the
// segment's "this is a person" intent. Substitute the unboxed Tabler person
// glyph (TbUser, already stocked in iconLibrary.js) instead, so every wheel —
// old or new — renders a clean unboxed person icon in its place.
const BOXED_ICON_SUBSTITUTES = {
  FaIdBadge: "TbUser",
  FaIdCard: "TbUser",
};

// Persisted identifier -> React element for the engine (d.data.icon) and the
// picker's selected-state display. Empty / unknown -> null (the engine then
// falls back to its default icon when the wheel-level master toggle is on).
export const iconElementFromName = (name) => {
  if (!name || typeof name !== "string") return null;
  const resolvedName = BOXED_ICON_SUBSTITUTES[name] || name;
  const Comp = iconComponentByName[resolvedName];
  return Comp ? <Comp /> : null;
};

// True when `name` is a non-empty identifier that resolves to a known icon.
export const isKnownIconName = (name) =>
  Boolean(name) && Boolean(iconComponentByName[name]);
